import { createRef, useEffect, useState, forwardRef, useImperativeHandle } from "react"
import PropTypes from "prop-types"
import { api } from "utils/api"
import { isBlank } from "utils/helpers"
import Trix from "trix"

document.addEventListener("trix-before-initialize", () => {
  Trix.config.blockAttributes.default.tagName = "p"
  Trix.config.attachments.preview.caption.name = false
  Trix.config.attachments.preview.caption.size = false
})

const RichTextEditor = forwardRef(
  (
    {
      name = "richTextEditor",
      value = "",
      onAttachmentAdd,
      onChange,
      destroyAttachmentsOnRemove = false
    },
    ref
  ) => {
    useImperativeHandle(ref, () => ({
      handleAttachments: handleAttachmentsDelete
    }))

    const [removeAttachments, setRemoveAttachments] = useState([])

    const trixEditor = createRef(null)

    useEffect(() => {
      if (!trixEditor.current) {
        return
      }

      trixEditor.current.addEventListener("trix-file-accept", handleFileAccept)
      trixEditor.current.addEventListener("trix-change", handleChange)
      trixEditor.current.addEventListener("trix-attachment-add", handleAttachmentAdd)
      trixEditor.current.addEventListener("trix-attachment-remove", handleAttachmentRemove)

      return () => {
        if (!trixEditor.current) {
          return
        }

        trixEditor.current.removeEventListener("trix-file-accept", handleFileAccept)
        trixEditor.current.removeEventListener("trix-change", handleChange)
        trixEditor.current.removeEventListener("trix-attachment-add", handleAttachmentAdd)
        trixEditor.current.removeEventListener("trix-attachment-remove", handleAttachmentRemove)
      }
    }, [])

    const handleFileAccept = event => {
      const acceptedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"]

      if (!acceptedTypes.includes(event.file.type)) {
        event.preventDefault()
        onAttachmentAdd({
          type: "error",
          errors: "Attachment must be jpeg, png or gif",
          attributes: null
        })
      }

      if (event.file.size >= 1024 * 1024 * 10) {
        event.preventDefault()
        onAttachmentAdd({
          type: "error",
          errors: "Attachment must be smaller than 10mb",
          attributes: null
        })
      }
    }

    const uploadFile = attachment => {
      api
        .upload("/image_uploads", attachment.file, {
          progressCallback: progress => attachment.setUploadProgress(progress)
        })
        .then(resp => {
          onAttachmentAdd &&
            onAttachmentAdd({
              type: "success",
              attributes: {
                id: resp.image.id,
                url: resp.image.url,
                href: `${resp.image.url}?content-disposition=attachment`
              }
            })

          attachment.setAttributes({
            id: resp.image.id,
            url: resp.image.url,
            href: `${resp.image.url}?content-disposition=attachment`
          })
        })
        .catch(error => {
          onAttachmentAdd &&
            onAttachmentAdd({
              type: "error",
              errors: error.errors || "Changes not saved",
              attributes: null
            })
        })
    }

    const handleChange = e => onChange && onChange(e)

    const handleAttachmentAdd = e => {
      if (!e.attachment.file) {
        const attributes = e.attachment.getAttributes()
        const attachments = removeAttachments.filter(id => id !== attributes.id)
        setRemoveAttachments(attachments)
      } else {
        onAttachmentAdd &&
          onAttachmentAdd({
            type: "uploading",
            attributes: e.attachment.getAttributes()
          })

        uploadFile(e.attachment)
      }
    }

    const handleAttachmentRemove = e => {
      const attributes = e.attachment.getAttributes()

      if (destroyAttachmentsOnRemove) {
        destroyAttachment(attributes.id)
      } else {
        setRemoveAttachments([...removeAttachments, attributes.id])
      }
    }

    const handleAttachmentsDelete = async () => {
      if (isBlank(removeAttachments)) {
        return Promise.resolve()
      }

      return await Promise.all(
        removeAttachments.map(attachment => destroyAttachment(attachment))
      ).then(setRemoveAttachments([]))
    }

    const destroyAttachment = attachment =>
      api.del(`/image_uploads/${attachment}`).catch(_e => undefined)

    return (
      <div>
        <input type="hidden" id={`${name}-input`} name={name} defaultValue={value} />
        <trix-editor
          id={name}
          input={`${name}-input`}
          className="trix-content"
          class="trix-content"
          ref={trixEditor}
          style={{ minHeight: "250px" }}
        />
      </div>
    )
  }
)

RichTextEditor.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onAttachmentAdd: PropTypes.func,
  destroyAttachmentsOnRemove: PropTypes.bool
}

RichTextEditor.displayName = "RichTextEditor"
export default RichTextEditor
